/* eslint-disable no-unused-vars */
import React from 'react';
import { CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/solid';

const WorkflowProgressIndicator = ({ currentStatus, registrationFee, eventName }) => {
  const stages = [
    { id: 'applied', label: 'Applied', description: 'Application submitted' },
    { id: 'approved', label: 'Approved', description: 'Awaiting payment' },
    { id: 'registered', label: 'Registered', description: 'Registration complete' },
  ];

  const getStageIndex = (status) => {
    const statusMap = {
      'Applied': 0,
      'Approved': 1,
      'Registered': 2,
      'Paid': 2,
    };
    return statusMap[status] ?? -1;
  };

  const currentIndex = getStageIndex(currentStatus);

  return (
    <div className="w-full">
      {/* Workflow Progress */}
      <div className="mb-8 bg-grey-50 rounded-lg p-6 border border-grey-200">
        {/* Event Context */}
        <div className="mb-6 pb-6 border-b border-grey-200">
          <h3 className="text-sm font-semibold text-grey-500 uppercase tracking-wide mb-2">Event</h3>
          <p className="text-lg font-semibold text-grey-900">{eventName}</p>
        </div>

        {/* Progress Stages */}
        <div className="flex items-center justify-between">
          {stages.map((stage, index) => (
            <div key={stage.id} className="flex items-center flex-1">
              {/* Stage Circle */}
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                    index < currentIndex
                      ? 'bg-green-100 text-green-700 border-2 border-green-500'
                      : index === currentIndex
                      ? 'bg-primary-100 text-primary-700 border-2 border-primary-500'
                      : 'bg-grey-200 text-grey-500 border-2 border-grey-300'
                  }`}
                >
                  {index < currentIndex ? (
                    <CheckCircleIcon className="w-6 h-6" />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </div>
                <p className="text-xs font-semibold text-grey-900 mt-2 text-center">{stage.label}</p>
                <p className="text-xs text-grey-600 text-center mt-1">{stage.description}</p>
              </div>

              {/* Connecting Arrow */}
              {index < stages.length - 1 && (
                <div
                  className={`flex-1 h-1 mx-2 transition-all ${
                    index < currentIndex ? 'bg-green-500' : 'bg-grey-300'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Current Status Message */}
        <div className="mt-6 pt-6 border-t border-grey-200">
          {currentStatus === 'Applied' && (
            <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-blue-600 text-lg">⏳</div>
              <div>
                <p className="font-semibold text-blue-900">Waiting for Approval</p>
                <p className="text-sm text-blue-700">The event organizer is reviewing your application. You'll be notified once approved.</p>
              </div>
            </div>
          )}
          {currentStatus === 'Approved' && (
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="text-amber-600 text-lg">⚠️</div>
              <div>
                <p className="font-semibold text-amber-900">Ready to Pay</p>
                <p className="text-sm text-amber-700">Your application was approved! Complete your vendor registration by paying the registration fee.</p>
              </div>
            </div>
          )}
          {currentStatus === 'Registered' && (
            <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-green-600 text-lg">✅</div>
              <div>
                <p className="font-semibold text-green-900">Registration Complete</p>
                <p className="text-sm text-green-700">You're registered for this event! Check your email for confirmation and event details.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fee Breakdown */}
      {registrationFee && (
        <div className="bg-white border border-grey-200 rounded-lg p-6 mb-6">
          <h3 className="text-sm font-semibold text-grey-500 uppercase tracking-wide mb-4">Fee Breakdown</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-grey-700">Registration Fee</span>
              <span className="font-semibold text-grey-900">${(registrationFee / 100).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b border-grey-200">
              <span className="text-grey-700 text-sm">Platform Fee (15%)</span>
              <span className="font-semibold text-grey-900">${((registrationFee * 0.15) / 100).toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center pt-3 bg-grey-50 rounded p-3">
              <span className="font-semibold text-grey-900">Your Payout</span>
              <span className="text-lg font-bold text-primary-600">${((registrationFee * 0.85) / 100).toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowProgressIndicator;
